import { IListColumn, AccountUserList } from '@global/types'
import { Template, FieldSetsWithFieldsResponse } from '@templates/types'
import { GET, POST } from './endpoints'
import {
  IAccountListsUpload,
  IAccountListsUploadResponse,
  IDatasetMetadataResponse,
  IDatasetPendingResponse,
  IDatasetQueryArgs,
  IDatasetQueryResponse,
  IGlobalPendingRollupResponse,
  IPendingDatasetGlobalResponse,
  IPendingRollupResponse,
  IReportsErrorRateResponse,
  ISubmissionsUsersResponse,
  ITemplateErrorRateResponse,
  ListExportArgs,
  PendingRowsData,
  PendingRowsArgs,
  ClassificationsArgs,
  ClassificationsData,
  ClassificationRowStatusArgs,
  ClassificationRowStatusData,
  IPublicAPIKeyResponse,
  IIntegrationStreamResponse,
  AuthHint,
} from './types'
import { MakeQueryOptionsDtoReturn } from './types/queryOptions'
import { fetchData, postData } from './utils'

export async function getUserIdsDraftWasSharedWith({
  draftId,
  signal,
}: {
  draftId: string
  signal?: AbortSignal
}) {
  const endpoint = GET.userIdsDraftWasSharedWIth(draftId)
  const data = await fetchData<PendingRowsData>({ endpoint, signal })

  return data
}

export async function getPendingRows({ datasetId, signal }: PendingRowsArgs) {
  if (!datasetId) return []

  const endpoint = GET.pendingRows(datasetId)
  const data = await fetchData<PendingRowsData>({ endpoint, signal })
  const { rows } = data

  return rows
}

export async function getDatasetMetadata({
  datasetId,
  signal,
}: {
  datasetId: string
  signal?: AbortSignal
}): Promise<IDatasetMetadataResponse | never[]> {
  if (!datasetId) return Promise.resolve([])

  const endpoint = GET.datasetMetadata(datasetId)
  const data = await fetchData<IDatasetMetadataResponse>({ endpoint, signal })

  return data
}

export async function getDatasetPending({
  templateId,
  signal,
}: {
  templateId: string
  signal?: AbortSignal
}): Promise<IDatasetPendingResponse[] | never[]> {
  if (!templateId) return Promise.resolve([])

  const endpoint = GET.datasetPending(templateId)
  const data = await fetchData<IDatasetPendingResponse[]>({ endpoint, signal })

  return data
}

export async function getPendingRollup({
  templateId,
  signal,
}: {
  templateId: string
  signal?: AbortSignal
}): Promise<IPendingRollupResponse[] | never[]> {
  if (!templateId) return Promise.resolve([])

  const endpoint = GET.pendingRollup(templateId)
  const data = await fetchData<IPendingRollupResponse[]>({ endpoint, signal })

  return data
}

export async function getPendingDatasetGlobal({
  signal,
}: {
  signal?: AbortSignal
}): Promise<IPendingDatasetGlobalResponse[] | never[]> {
  const endpoint = GET.pendingDatasetGlobal()
  const data = await fetchData<IPendingDatasetGlobalResponse[]>({ endpoint, signal })

  return data
}

export async function getPendingRollupGlobal({
  signal,
}: {
  signal?: AbortSignal
}): Promise<IGlobalPendingRollupResponse[] | never[]> {
  const endpoint = GET.pendingRollupGlobal()
  const data = await fetchData<IGlobalPendingRollupResponse[]>({ endpoint, signal })

  return data
}

export async function getPublicApiKey({ feature }: { feature: string }) {
  const endpoint = POST.publicApiKey(feature)
  const data = await postData<IPublicAPIKeyResponse, string>({ endpoint })

  return data
}

export async function getAccountAuthHint({ signal }: { signal?: AbortSignal }) {
  const endpoint = GET.accountAuthHint()
  const data = await fetchData<AuthHint>({ endpoint, signal })

  return data
}

export async function getAcademyUrl() {
  const endpoint = POST.academy()
  const data = await postData<string, string>({ endpoint })

  return data
}

export async function getIntegrationAsset({ datasetId, cvId, signal }: any) {
  if (!datasetId) return []

  const endpoint = GET.integrationAsset({ datasetId, cvId })
  const data = await fetchData<any>({ endpoint, signal })

  return data
}

export async function getClassificationRowStatus({
  datasetId,
  signal,
}: ClassificationRowStatusArgs) {
  if (!datasetId) return []

  const endpoint = GET.classificationRowStatus(datasetId)
  const data = await fetchData<ClassificationRowStatusData>({ endpoint, signal })

  return data
}

export async function getCreativeClassifications({ signal }: ClassificationsArgs) {
  const endpoint = GET.creativeClassifications()
  const classifications = await fetchData<ClassificationsData>({ endpoint, signal })

  return classifications
}

export async function templatesWithFolder({ signal }: { signal?: AbortSignal }) {
  const endpoint = GET.templatesWithFolder()
  const data = await fetchData<Template[]>({ endpoint, signal })

  return data
}

export async function getAccountUserList({ signal }: { signal?: AbortSignal }) {
  const endpoint = GET.accountUserList()
  const data = await fetchData<AccountUserList[]>({ endpoint, signal })

  return data
}

export async function getLists({ signal }: { signal?: AbortSignal }) {
  const endpoint = GET.lists()
  const data = await fetchData<IListColumn[]>({ endpoint, signal })

  return data
}

export async function getListExport({ listId, signal }: ListExportArgs) {
  if (!listId) return null

  const endpoint = GET.listExport(listId)
  const response = await fetchData<string>({ endpoint, signal })

  return response
}

export async function getList<T>({ listId, signal }: { listId: string; signal?: AbortSignal }) {
  const endpoint = GET.list(listId)
  const data = await fetchData<T>({ endpoint, signal })
  return data
}

export async function getListsUploadStatus<T>({
  listId,
  signal,
}: {
  listId: string
  signal?: AbortSignal
}) {
  const endpoint = GET.accountListsUploadStatus(listId)
  const data = await fetchData<T>({ endpoint, signal })

  return data
}

/**
 * Datasets Query Start
 */
export async function getDatasetsQuery({
  datasetId,
  queryOptions,
  axiosOptions,
  errorCallback,
}: IDatasetQueryArgs): Promise<IDatasetQueryResponse> {
  const endpoint = POST.datasetsQuery(datasetId)
  const data = await postData<IDatasetQueryResponse, MakeQueryOptionsDtoReturn>({
    endpoint,
    queryOptions,
    axiosOptions,
    errorCallback,
  })
  return data
}

/**
 * Upload file to account lists.
 * - This is a multipart/form-data request.
 * - There are callbacks for onUploadProgress and errorCallback.
 */
export async function accountListsFileUpload({
  formDataFields,
  onUploadProgress,
  errorCallback,
}: IAccountListsUpload): Promise<IAccountListsUploadResponse> {
  const endpoint = POST.accountListsUpload()
  const { file, listType, listId, listName, notes } = formDataFields

  const formData = new FormData()
  formData.append('file', file)
  formData.append('name', listName)
  formData.append('type', listType)
  formData.append('listId', listId)
  formData.append('notes', notes || '')

  const response = await postData<IAccountListsUploadResponse, FormData>({
    endpoint,
    queryOptions: formData,
    axiosOptions: {
      onUploadProgress,
    },
    errorCallback,
  })

  return response
}

export async function getTemplate<T>({
  signal,
  templateID,
}: {
  templateID: string | null
  signal?: AbortSignal
}): Promise<T | null> {
  if (!templateID) return Promise.resolve(null)

  const endpoint = GET.template(templateID)
  const data = await fetchData<T>({ endpoint, signal })

  return data
}

export async function getDailyIntegrationsReport({
  signal,
  params,
}: {
  signal?: AbortSignal
  params: { from: string; to: string }
}): Promise<IIntegrationStreamResponse> {
  const endpoint = GET.dailyIntegrationsReport()
  const data = await fetchData<IIntegrationStreamResponse>({ endpoint, params, signal })
  return data
}

export async function getTemplateErrorRate({
  signal,
  numberOfDaysToFilter,
  isFilteredByRow,
}: {
  signal?: AbortSignal
  numberOfDaysToFilter?: string
  isFilteredByRow: number
}): Promise<ITemplateErrorRateResponse[]> {
  const endpoint = GET.templateErrorRate()
  const params = numberOfDaysToFilter
    ? { days: numberOfDaysToFilter, rows: isFilteredByRow }
    : { rows: isFilteredByRow }
  const data = await fetchData<ITemplateErrorRateResponse[]>({ endpoint, params, signal })
  return data
}

export async function getReportsErrorRate({
  signal,
  numberOfDaysToFilter,
  isFilteredByRow,
}: {
  signal?: AbortSignal
  numberOfDaysToFilter?: string
  isFilteredByRow: number
}): Promise<IReportsErrorRateResponse[]> {
  const endpoint = GET.reportsErrorRate()
  const params = numberOfDaysToFilter
    ? { days: numberOfDaysToFilter, rows: isFilteredByRow }
    : { rows: isFilteredByRow }
  const data = await fetchData<IReportsErrorRateResponse[]>({ endpoint, params, signal })
  return data
}

export async function getSubmissionsUsers({
  signal,
  numberOfDaysToFilter,
}: {
  signal?: AbortSignal
  numberOfDaysToFilter?: string
}): Promise<ISubmissionsUsersResponse[]> {
  const endpoint = GET.submissionsUsers()
  const params = numberOfDaysToFilter ? { days: numberOfDaysToFilter } : {}
  const data = await fetchData<ISubmissionsUsersResponse[]>({ endpoint, params, signal })
  return data
}

export async function getFieldsetsWithFields({
  signal,
}: {
  signal?: AbortSignal
}): Promise<FieldSetsWithFieldsResponse[]> {
  const endpoint = GET.fieldsetsWithFields()
  const data = await fetchData<FieldSetsWithFieldsResponse[]>({ endpoint, signal })
  return data
}

export async function mfaCheck({ email, mfa_token }: { email: string; mfa_token: string }) {
  const endpoint = POST.mfaCheck()
  const data = await postData<any, any>({
    endpoint,
    queryOptions: { email, mfa_token },
    axiosOptions: { withCredentials: false },
  })

  return data
}

export async function mfaChallenge({ email, mfa_token }: { email: string; mfa_token: string }) {
  const endpoint = POST.mfaChallenge()
  const data = await postData<any, any>({
    endpoint,
    queryOptions: { email, mfa_token },
    axiosOptions: { withCredentials: false },
  })

  return data
}

export async function enableMfa({
  email,
  mfa_token,
  code,
  secret,
}: {
  email: string
  mfa_token: string
  code: string
  secret: string
}) {
  const endpoint = POST.enableMfa()
  const data = await postData<any, any>({
    endpoint,
    queryOptions: { email, mfa_token, code, secret },
    axiosOptions: { withCredentials: false },
  })

  return data
}
